import { useRef, useState } from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import brandService from '../../services/brandService';
import imageService from '../../services/imageService';
import Modal from '../../components/Common/Modal';
import Input from '../../components/Common/Input';
import Button from '../../components/Common/Button';

const emptyForm = {
  nameEn: '',
  nameAr: '',
  descriptionEn: '',
  descriptionAr: '',
  countryOfOrigin: '',
  website: '',
  note: '',
};

const CreateBrandDialog = ({ isOpen, onClose, onSuccess }) => {
  const { get, isRtl } = useLocalization();
  const [formData, setFormData] = useState(emptyForm);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const logoInputRef = useRef(null);

  const resetForm = () => {
    setFormData(emptyForm);
    setLogoFile(null);
    setLogoPreview(null);
  };

  const handleLogoSelect = (files) => {
    const file = files?.[0];
    if (!file) return;
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = () => setLogoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      let logoUrl;
      if (logoFile) {
        const uploadResult = await imageService.uploadImages([logoFile], 'brands');
        logoUrl = uploadResult.imageUrls?.[0];
      }

      await brandService.create({
        nameEn: formData.nameEn,
        nameAr: formData.nameAr,
        descriptionEn: formData.descriptionEn || null,
        descriptionAr: formData.descriptionAr || null,
        countryOfOrigin: formData.countryOfOrigin || null,
        website: formData.website || null,
        note: formData.note || null,
        logoUrl: logoUrl || null,
      });
      resetForm();
      onSuccess();
    } catch (error) {
      console.error('Error creating brand:', error);
      alert(isRtl ? 'حدث خطأ أثناء إنشاء العلامة التجارية' : 'Error creating brand');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={get('add_new_brand')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {get('cancel')}
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isLoading}>
            {get('save')}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        <Input
          label={isRtl ? 'الاسم (إنجليزي)' : 'Name (English)'}
          value={formData.nameEn}
          onChange={(e) => setFormData({ ...formData, nameEn: e.target.value })}
          required
        />
        <Input
          label={isRtl ? 'الاسم (عربي)' : 'Name (Arabic)'}
          value={formData.nameAr}
          onChange={(e) => setFormData({ ...formData, nameAr: e.target.value })}
          required
        />
        <Input
          label={isRtl ? 'بلد المنشأ' : 'Country of Origin'}
          value={formData.countryOfOrigin}
          onChange={(e) => setFormData({ ...formData, countryOfOrigin: e.target.value })}
        />
        <Input
          label={isRtl ? 'الموقع الإلكتروني' : 'Website'}
          type="url"
          placeholder="https://"
          value={formData.website}
          onChange={(e) => setFormData({ ...formData, website: e.target.value })}
        />
        <div className="form-group">
          <label className="form-label">{isRtl ? 'الوصف (إنجليزي)' : 'Description (English)'}</label>
          <textarea
            className="form-textarea"
            value={formData.descriptionEn}
            onChange={(e) => setFormData({ ...formData, descriptionEn: e.target.value })}
            rows="3"
          />
        </div>
        <div className="form-group">
          <label className="form-label">{isRtl ? 'الوصف (عربي)' : 'Description (Arabic)'}</label>
          <textarea
            className="form-textarea"
            value={formData.descriptionAr}
            onChange={(e) => setFormData({ ...formData, descriptionAr: e.target.value })}
            rows="3"
          />
        </div>
        <div className="form-group">
          <label className="form-label">{isRtl ? 'ملاحظة' : 'Note'}</label>
          <textarea
            className="form-textarea"
            value={formData.note}
            onChange={(e) => setFormData({ ...formData, note: e.target.value })}
            rows="2"
          />
        </div>
        <div className="form-group">
          <label className="form-label">{isRtl ? 'شعار العلامة التجارية' : 'Brand Logo'}</label>
          <input
            type="file"
            accept="image/*"
            ref={logoInputRef}
            onChange={(e) => handleLogoSelect(e.target.files)}
            hidden
          />
          <div className="d-flex gap-4" style={{ alignItems: 'center' }}>
            <Button variant="secondary" type="button" size="small" onClick={() => logoInputRef.current?.click()}>
              {isRtl ? 'رفع شعار' : 'Upload Logo'}
            </Button>
            {logoPreview && (
              <Button variant="danger" type="button" size="small" onClick={handleRemoveLogo}>
                {isRtl ? 'إزالة' : 'Remove'}
              </Button>
            )}
          </div>
          {logoPreview && (
            <div className="image-grid mt-2">
              <div className="image-item">
                <img src={logoPreview} alt="Brand logo preview" />
              </div>
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default CreateBrandDialog;
